// Single source of truth for the product-type taxonomy, shared by the public
// listing filter, the data store and the admin editor. Keeping it here avoids
// three copies of the same lists drifting apart.

import type { Product, ProductType } from '@/app/products/productsData';

/** The product-type facet, in display/filter order. */
export const PRODUCT_TYPES: ProductType[] = [
  'Plates',
  'Bowls',
  'Trays',
  'Containers',
  'Cups and lids',
  'Dips',
  'Cutlery',
];

/** New type facet -> legacy SEO `category` (productSchema + card icon). */
export const TYPE_TO_CATEGORY: Record<ProductType, Product['category']> = {
  Plates: 'plates',
  Bowls: 'bowls',
  Trays: 'trays',
  Containers: 'trays',
  'Cups and lids': 'cups',
  Dips: 'bowls',
  Cutlery: 'cutlery',
};

/** Legacy ?category= deep-links / old rows -> the nearest new type. */
export const CATEGORY_TO_TYPE: Record<string, ProductType> = {
  plates: 'Plates',
  bowls: 'Bowls',
  trays: 'Trays',
  cups: 'Cups and lids',
  cutlery: 'Cutlery',
};

/** Silhouette hints used for placeholders / related. */
export const PRODUCT_SHAPES = [
  'round',
  'bowl',
  'square',
  'rect',
  'cup',
  'cutlery',
  'floral',
  'oval',
  'boat',
] as const;

/** Material/supplier line options for the admin select. */
export const MATERIAL_OPTIONS: { value: 'bagasse' | 'areca'; label: string }[] = [
  { value: 'bagasse', label: 'Sugarcane (bagasse)' },
  { value: 'areca', label: 'Areca palm leaf' },
];

/** Narrow free text to a known ProductType, else undefined. */
export function toProductType(v: string | null | undefined): ProductType | undefined {
  return v && (PRODUCT_TYPES as string[]).includes(v) ? (v as ProductType) : undefined;
}

/** The legacy category for a type (falls back to an existing value, else 'custom'). */
export function categoryForType(
  type: string | null | undefined,
  fallback?: string
): Product['category'] {
  const t = toProductType(type);
  if (t) return TYPE_TO_CATEGORY[t];
  return (fallback as Product['category']) || 'custom';
}
